import { Button } from "@opencode/ui/button"
import { useDialog } from "@opencode/ui/context/dialog"
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitleGroup } from "@opencode/ui/dialog"
import { Icon } from "@opencode/ui/icon"
import { useAddProjects } from "@/home/model"
import { useLanguage } from "@/runtime/i18n/language"
import { usePlatform } from "@/runtime/platform/platform"
import { ServerConnection } from "@/runtime/server/registry"
import { useGlobal } from "@/runtime/server/runtime"
import { useDirectoryPicker } from "@/workspaces/selection/picker"
import { useRemoteWorkspace } from "@/runtime/server/remote"
import { RemoteProjectDialog } from "./remote-project"

export function useProjectEntry() {
  const dialog = useDialog()
  const language = useLanguage()
  const global = useGlobal()
  const platform = usePlatform()
  const pickDirectory = useDirectoryPicker()
  const addProjects = useAddProjects()
  const remote = useRemoteWorkspace()

  const open = (server: ServerConnection.Any, onAdded?: (directory: string) => void) => {
    if (server.type === "remote") {
      openRemote((_, directory) => onAdded?.(directory))
      return
    }
    pickDirectory({
      server,
      title: language.t("home.project.add"),
      onSelect: (value) => {
        const directory = Array.isArray(value) ? value[0] : value
        if (!directory) return
        addProjects(server, [directory])
        onAdded?.(directory)
      },
    })
  }

  const canRemote = () => remote.available()
  const openRemote = (onAdded?: (server: ServerConnection.Any, directory: string) => void) => {
    if (!canRemote()) return
    dialog.show(() => (
      <RemoteProjectDialog
        onAdded={(server, directory) => {
          addProjects(server, [directory])
          onAdded?.(server, directory)
        }}
      />
    ))
  }

  const choose = (
    server: ServerConnection.Any,
    onAdded?: (server: ServerConnection.Any, directory: string) => void,
  ) => {
    if (platform.cloudWorkspace) {
      openRemote(onAdded)
      return
    }
    const local =
      platform.platform === "desktop"
        ? ServerConnection.local(server)
          ? server
          : (global.servers.list().find(ServerConnection.builtin) ?? global.servers.list().find(ServerConnection.local))
        : server
    const available = (target?: ServerConnection.Any) =>
      !!target && global.ensureServerCtx(target).sdk.connection.status() === "connected"
    const select = (target?: ServerConnection.Any) => {
      if (!target || !available(target)) return
      dialog.close()
      queueMicrotask(() => open(target, (directory) => onAdded?.(target, directory)))
    }
    dialog.show(() => (
      <Dialog fit containerClass="sair-project-dialog-container" class="sair-project-dialog">
        <DialogHeader>
          <DialogTitleGroup
            title={language.t("home.project.add")}
            description={language.t("project.entry.description")}
          />
        </DialogHeader>
        <DialogBody>
          <div class="flex flex-col gap-2">
            <Button
              data-action="project-entry-local"
              variant="neutral"
              disabled={!available(local)}
              onClick={() => select(local)}
            >
              <Icon
                name={platform.platform === "desktop" || ServerConnection.local(local) ? "folder" : "folder-remote"}
                size="small"
              />
              {language.t(
                platform.platform === "desktop" ? "project.entry.addLocal" : "project.entry.addCurrentServer",
              )}
            </Button>
            <Button
              data-action="project-entry-remote"
              variant="neutral"
              disabled={!canRemote()}
              onClick={() => {
                dialog.close()
                queueMicrotask(() => openRemote(onAdded))
              }}
            >
              <Icon name="folder-remote" size="small" />
              {language.t("project.entry.addRemote")}
            </Button>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="neutral" onClick={() => dialog.close()}>
            {language.t("common.cancel")}
          </Button>
        </DialogFooter>
      </Dialog>
    ))
  }

  return { open, choose, canRemote, openRemote }
}
